import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  Link,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  VisibilityOff as VisibilityOffIcon, 
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { 
  developerFeedbackService, 
  DeveloperFeedback,
  DeveloperFeedbackResponse,
} from '../../services/api/developerFeedback.service';
import { format } from 'date-fns';

const DeveloperFeedbackPage: React.FC = () => {
  // Form state
  const [title, setTitle] = useState('');
  const [feedback, setFeedback] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Feedback list state
  const [feedbackList, setFeedbackList] = useState<DeveloperFeedback[]>([]);
  const [totalFeedback, setTotalFeedback] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  
  const { user } = useSelector((state: RootState) => state.auth);

  // Fetch feedback when tab, page, or rowsPerPage changes
  useEffect(() => {
    if (tabValue === 1) {
      fetchFeedbackList();
    }
  }, [tabValue, page, rowsPerPage]);

  const fetchFeedbackList = async () => {
    try {
      setLoadingFeedback(true);
      const response = await developerFeedbackService.getFeedback(
        page + 1, 
        rowsPerPage,
        user?.email // Pass the current user's email to filter by user
      );
      setFeedbackList(response.data);
      setTotalFeedback(response.total);
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('Failed to load feedback list');
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      
      // Check file size (limit to 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      
      // Check file type
      if (!selectedFile.type.match('image.*')) {
        setError('Please select an image file');
        return;
      }
      
      setImage(selectedFile);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }
    
    if (!feedback.trim()) {
      setError('Please enter your feedback');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('feedback', feedback);
      formData.append('user_email', user?.email || 'anonymous');
      
      if (image) {
        formData.append('image', image);
      }
      
      // Use the service to submit feedback
      await developerFeedbackService.createFeedback(formData);
      
      // Reset form
      setTitle('');
      setFeedback('');
      setImage(null);
      setImagePreview(null);
      setSuccess(true);
      
      // Refresh feedback list if on that tab
      if (tabValue === 1) {
        fetchFeedbackList();
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(null);
  };

  const handleViewImage = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
  };

  const handleCloseImageDialog = () => {
    setSelectedImageUrl(null);
  };

  const getStatusChip = (status: DeveloperFeedback['status']) => {
    switch (status) {
      case 'pending':
        return (
          <Chip
            icon={<ScheduleIcon />}
            label="Pending"
            color="default"
            size="small"
            sx={{ borderRadius: '4px' }}
          />
        );
      case 'reviewed':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Reviewed"
            color="primary"
            size="small"
            variant="outlined"
            sx={{ borderRadius: '4px' }}
          />
        );
      case 'implemented':
        return (
          <Chip
            icon={<DoneIcon />}
            label="Implemented"
            color="success"
            size="small"
            sx={{ borderRadius: '4px' }}
          />
        );
      case 'rejected':
        return (
          <Chip
            icon={<CancelIcon />}
            label="Rejected"
            color="error"
            size="small"
            variant="outlined"
            sx={{ borderRadius: '4px' }}
          />
        );
      default:
        return null;
    }
  };

  const renderSubmitFeedbackTab = () => (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      <Grid item xs={12} md={8}>
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Submit Your Feedback
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                label="Title"
                fullWidth
                margin="normal"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="Brief summary of your feedback"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SendIcon color="primary" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Feedback"
                fullWidth
                multiline
                rows={6}
                margin="normal"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                required
                placeholder="Describe your suggestion, feature request, or issue in detail"
                variant="outlined"
              />

              <Box sx={{ mt: 3, mb: 3 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mr: 2 }}
                  >
                    Attach Screenshot
                  </Button>
                </label>
                {imagePreview && (
                  <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.7)',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{ mt: 2 }}
              >
                {loading ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              How Your Feedback Helps
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Improve User Experience
              </Typography>
              <Typography variant="body2" paragraph>
                Your insights help us create a more intuitive and efficient system.
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Fix Issues
              </Typography>
              <Typography variant="body2" paragraph>
                Report bugs or problems you encounter so we can resolve them quickly.
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Suggest Features
              </Typography>
              <Typography variant="body2">
                Share ideas for new features that would make your work easier.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFeedbackListTab = () => (
    <Card elevation={3} sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Your Submitted Feedback
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {loadingFeedback ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : feedbackList.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            You haven't submitted any feedback yet.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table sx={{ minWidth: 650 }} aria-label="feedback table">
                <TableHead sx={{ bgcolor: 'background.default' }}>
                  <TableRow>
                    <TableCell width="30%"><Typography fontWeight="bold">Title</Typography></TableCell>
                    <TableCell width="40%"><Typography fontWeight="bold">Feedback</Typography></TableCell>
                    <TableCell width="15%"><Typography fontWeight="bold">Date</Typography></TableCell>
                    {/* <TableCell width="10%"><Typography fontWeight="bold">Status</Typography></TableCell> */}
                    <TableCell width="5%"><Typography fontWeight="bold">Image</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedbackList.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxHeight: '80px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.feedback.length > 150
                            ? `${item.feedback.substring(0, 150)}...`
                            : item.feedback}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(item.created_at), 'MMM d, yyyy')}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>
                        {getStatusChip(item.status)}
                      </TableCell> */}
                      <TableCell>
                        {item.image_url ? (
                          <Tooltip title="View Image">
                            <IconButton
                              size="small"
                              onClick={() => handleViewImage(item.image_url!)}
                              color="primary"
                            >
                              <ImageIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <VisibilityOffIcon fontSize="small" color="disabled" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalFeedback}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        Developer Feedback
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Help us improve the Customer Feedback Management System by sharing your thoughts, suggestions, or reporting issues.
      </Typography>

      <Tabs 
        value={tabValue} 
        onChange={handleTabChange}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
          }
        }}
      >
        <Tab label="Submit Feedback" />
        <Tab label="My Submissions" />
      </Tabs>

      {tabValue === 0 && renderSubmitFeedbackTab()}
      {tabValue === 1 && renderFeedbackListTab()}

      {/* Image Dialog */}
      {selectedImageUrl && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
          onClick={handleCloseImageDialog}
        >
          <Box sx={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
            <IconButton
              sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
              }}
              onClick={handleCloseImageDialog}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={selectedImageUrl}
              alt="Feedback attachment"
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 100px)',
                borderRadius: '4px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              }}
            />
          </Box>
        </Box>
      )}

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          Thank you! Your feedback has been submitted successfully.
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeveloperFeedbackPage; 